import { QueryClient } from "@tanstack/react-query";
import { IS_PROD } from "../config/config";
import { authManager } from "../data/managers/AuthManager";
import { HttpMethodEnum } from "../data/models/HttpMethodEnum";

export interface ApiResponse<T> {
  data: T | undefined;
  isSuccess: boolean;
  messageError?: string;
  message?: string;
}

export class RequestBuilder {
  private queryClient: QueryClient;
  private baseUrl = this.getBaseUrl();

  constructor() {
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: { staleTime: 0 },
      },
    });
  }

  private getBaseUrl(): string {
    const fallbackUrl = IS_PROD
      ? "https://api-server-1068774953588.us-central1.run.app"
      : "http://localhost:8080";
    const envUrl = process.env.REACT_APP_API_URL?.trim();
    const finalUrl = envUrl || fallbackUrl;
    return finalUrl.replace(/\/+$/, "");
  }

  private generateKey(
    method: HttpMethodEnum,
    url: string,
    params?: any
  ): string {
    return `${method}:${url}${params ? JSON.stringify(params) : ""}`;
  }

  private buildUrl(url: string, method: HttpMethodEnum, params?: any): string {
    if (method === HttpMethodEnum.GET && params) {
      const queryString = new URLSearchParams(params).toString();
      return `${this.baseUrl}${url}?${queryString}`;
    }
    return `${this.baseUrl}${url}`;
  }

  private async request<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any
  ): Promise<ApiResponse<T>> {
    const requestUrl = this.buildUrl(url, method, params);
    const authToken = authManager.getAuthToken();
    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: method !== HttpMethodEnum.GET ? JSON.stringify(params) : undefined,
    };
    try {
      const response = await fetch(requestUrl, options);
      const responseData = await response.json();
      if (!response.ok) {
        return {
          data: undefined,
          isSuccess: false,
          messageError: `Error ${response.status}: ${response.statusText}`,
          message: responseData.data || responseData.error || "Unknown error",
        };
      }
      return {
        data: responseData.data,
        isSuccess: true,
      };
    } catch (error: any) {
      console.error("Fetch error:", error);
      return {
        data: undefined,
        isSuccess: false,
        messageError: error.message || "Unknown error",
      };
    }
  }

  async requestWithCache<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any,
    staleTime = 0
  ): Promise<ApiResponse<T>> {
    const queryKey = [this.generateKey(method, url, params)];
    return this.queryClient.fetchQuery<ApiResponse<T>>({
      queryKey,
      queryFn: () => this.request<T>(method, url, params),
      staleTime,
    });
  }

  async requestHard<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any
  ): Promise<ApiResponse<T>> {
    const queryKey = this.generateKey(method, url, params);
    await this.queryClient.invalidateQueries({
      queryKey,
      refetchType: "active",
    });
    return this.request<T>(method, url, params);
  }

  createEndpoint<T>(
    method: HttpMethodEnum,
    url: string
  ): {
    request: (params?: any, staleTime?: number) => Promise<ApiResponse<T>>;
    requestHard: (params?: any) => Promise<ApiResponse<T>>;
  } {
    return {
      request: (params?: any, staleTime?: number): Promise<ApiResponse<T>> =>
        this.requestWithCache<T>(method, url, params, staleTime),
      requestHard: (params?: any): Promise<ApiResponse<T>> =>
        this.requestHard<T>(method, url, params),
    };
  }
}
